"use client";

import { useTexture } from "@react-three/drei";
import { type PropsWithChildren, useCallback, useLayoutEffect, useMemo } from "react";
import { MeshPhongMaterial, RepeatWrapping } from "three";
import { match } from "ts-pattern";
import { useConfig } from "../../../context/ConfigContext";
import { useCabinetStore } from "../../../context/StoreContext";
import { materialsSku, textureAssets } from "../../../library/utilities";
import { getCurrentThickness, isLongPartsBuildMethod } from "../data";
import { DOOR_MARGIN } from "../data/Door";
import { MAX_DOUBLE_DOOR_WIDTH } from "../data/DoubleDoor";
import { MaterialContext, type MaterialProps } from "./MaterialContext";

type MaterialContextProviderProps = PropsWithChildren;

export function MaterialContextProvider(props: MaterialContextProviderProps) {
	const { mode } = useConfig();
	const { state, api } = useCabinetStore();
	const thickness = getCurrentThickness(state, api);

	const textureAsset = useMemo(() => {
		return match(thickness?.sku)
			.returnType<{ map: string; specular?: string }>()
			.with(...materialsSku.black, () => textureAssets.black)
			.with(...materialsSku.oak, () => textureAssets.oak)
			.with(...materialsSku.mdf, () => textureAssets.mdf)
			.with(...materialsSku.fb_025_cst, () => textureAssets.fb_025_cst)
			.with(...materialsSku.fb_u147_cst, () => textureAssets.fb_u147_cst)
			.with(...materialsSku.fb_625_cst, () => textureAssets.fb_625_cst)
			.with(...materialsSku.fb_u146_cst, () => textureAssets.fb_u146_cst)
			.with(...materialsSku.fb_u115_cst, () => textureAssets.fb_u115_cst)
			.with(...materialsSku.fb_u129_cst, () => textureAssets.fb_u129_cst)
			.with(...materialsSku.fb_ud81_cst, () => textureAssets.fb_ud81_cst)
			.with(...materialsSku.fb_113_cst, () => textureAssets.fb_113_cst)
			.with(...materialsSku.fb_u311_bst, () => textureAssets.fb_u311_bst)
			.with(...materialsSku.fb_u653_bst, () => textureAssets.fb_u653_bst)
			.with(...materialsSku.fb_f256_m02, () => textureAssets.fb_f256_m02)
			.with(...materialsSku.fb_f257_m02, () => textureAssets.fb_f257_m02)
			.with(...materialsSku.fb_f261_m02, () => textureAssets.fb_f261_m02)
			.with(...materialsSku.fb_h251_w06, () => textureAssets.fb_h251_w06)
			.with(...materialsSku.fb_h397_bst, () => textureAssets.fb_h397_bst)
			.with(...materialsSku.fb_h906_w06, () => textureAssets.fb_h906_w06)
			.with(...materialsSku.fb_h597_w07, () => textureAssets.fb_h597_w07)
			.with(...materialsSku.fb_h265_v1a, () => textureAssets.fb_h265_v1a)
			.with(...materialsSku.fb_h267_v1a, () => textureAssets.fb_h267_v1a)
			.otherwise(() => textureAssets.mdf_prime);
	}, [thickness]);

	const texture = useTexture(textureAsset);

	useLayoutEffect(() => {
		match(thickness?.sku)
			.with(...materialsSku.oak, () => {
				texture.map.wrapS = texture.map.wrapT = RepeatWrapping;
				texture.map.repeat.setScalar(0.25);
			})
			.with(...materialsSku.fb_h251_w06, () => {
				texture.map.wrapS = texture.map.wrapT = RepeatWrapping;
				texture.map.repeat.setScalar(0.5);
			})
			.with(...materialsSku.fb_h906_w06, () => {
				texture.map.wrapS = texture.map.wrapT = RepeatWrapping;
				texture.map.repeat.setScalar(0.4);
			})
			.with(...materialsSku.fb_h397_bst, () => {
				texture.map.wrapS = texture.map.wrapT = RepeatWrapping;
				texture.map.repeat.setScalar(0.4);
			})
			.with(...materialsSku.fb_h597_w07, () => {
				texture.map.wrapS = texture.map.wrapT = RepeatWrapping;
				texture.map.repeat.setScalar(0.5);
			})
			.with(...materialsSku.fb_h265_v1a, () => {
				texture.map.wrapS = texture.map.wrapT = RepeatWrapping;
				texture.map.repeat.setScalar(0.75);
			})
			.with(...materialsSku.fb_h267_v1a, () => {
				texture.map.wrapS = texture.map.wrapT = RepeatWrapping;
				texture.map.repeat.setScalar(0.4);
			});
	}, [thickness, texture]);

	const material = useMemo(
		() =>
			match(thickness?.sku)
				.with(
					...materialsSku.black,
					() =>
						new MeshPhongMaterial({
							color: 0x000000,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.oak,
					() =>
						new MeshPhongMaterial({
							color: 0xb59d82,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(...materialsSku.mdf, () => ({
					top: new MeshPhongMaterial({
						color: 0xb39682,
						map: texture.map,
					}),
					side: new MeshPhongMaterial({
						color: 0x987f6e,
						map: texture.map,
					}),
				}))
				.with(...materialsSku.mdf_prime, () => ({
					top: new MeshPhongMaterial({
						color: 0xcecece,
						map: texture.map,
					}),
					side: new MeshPhongMaterial({
						color: 0xebe8e8,
						map: texture.map,
					}),
				}))
				.with(
					...materialsSku.fb_025_cst,
					() =>
						new MeshPhongMaterial({
							color: 0xfbfaf2,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_u147_cst,
					() =>
						new MeshPhongMaterial({
							color: 0xeee7e2,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_625_cst,
					() =>
						new MeshPhongMaterial({
							color: 0xc9c6c0,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_u146_cst,
					() =>
						new MeshPhongMaterial({
							color: 0xb0aaa2,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_u115_cst,
					() =>
						new MeshPhongMaterial({
							color: 0xa89f92,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_u129_cst,
					() =>
						new MeshPhongMaterial({
							color: 0x776c66,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_ud81_cst,
					() =>
						new MeshPhongMaterial({
							color: 0x5c5856,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_113_cst,
					() =>
						new MeshPhongMaterial({
							color: 0x1b1817,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_u311_bst,
					() =>
						new MeshPhongMaterial({
							color: 0x614035,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_u653_bst,
					() =>
						new MeshPhongMaterial({
							color: 0x79766c,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_f256_m02,
					() =>
						new MeshPhongMaterial({
							color: 0x614035,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_f257_m02,
					() =>
						new MeshPhongMaterial({
							color: 0x525357,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_f261_m02,
					() =>
						new MeshPhongMaterial({
							color: 0x5c5856,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_h251_w06,
					() =>
						new MeshPhongMaterial({
							color: 0x715440,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_h397_bst,
					() =>
						new MeshPhongMaterial({
							color: 0xa69381,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_h906_w06,
					() =>
						new MeshPhongMaterial({
							color: 0x95816d,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_h597_w07,
					() =>
						new MeshPhongMaterial({
							color: 0x483d32,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_h265_v1a,
					() =>
						new MeshPhongMaterial({
							color: 0x796952,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.with(
					...materialsSku.fb_h267_v1a,
					() =>
						new MeshPhongMaterial({
							color: 0x968064,
							map: texture.map,
							specularMap: texture.specular,
							specular: 0x555555,
							shininess: 5,
						}),
				)
				.otherwise(
					() =>
						new MeshPhongMaterial({
							color: 0xffffff,
						}),
				),
		[thickness, texture],
	);

	const defaultMaterial = useMemo(() => {
		const side = material instanceof MeshPhongMaterial ? material : material.side;
		const top = material instanceof MeshPhongMaterial ? material : material.top;

		return [side, side, top, top, side, side];
	}, [material]);

	const getIsError = useCallback(
		(materialProps: MaterialProps) => {
			const partsKey = materialProps.variant === "double-door" ? "door" : materialProps.variant;
			const constraints = thickness?.parts[partsKey].constraints;

			if (!constraints) {
				return true;
			}

			const size = match(materialProps)
				.with({ variant: "door" }, { variant: "double-door" }, (p) => ({
					width: p.depth,
					depth: p.width,
					height: p.height,
				}))
				.otherwise((p) => ({ width: p.width, depth: p.depth, height: p.height }));

			if (materialProps.variant === "background") {
				const smallestSide = isLongPartsBuildMethod(state.thickness)
					? size.depth
					: Math.min(size.width, size.depth);

				const largestSide = isLongPartsBuildMethod(state.thickness)
					? size.width
					: Math.max(size.width, size.depth);

				return (
					smallestSide < (constraints.width.min ?? 0) ||
					smallestSide > (constraints.width.max ?? 0) ||
					largestSide < (constraints.depth.min ?? 0) ||
					largestSide > (constraints.depth.max ?? 0)
				);
			}

			if (materialProps.variant === "door") {
				if (size.width > size.depth) return true;
			}

			if (materialProps.variant === "double-door") {
				if ((size.width + DOOR_MARGIN - 1) * 2 > MAX_DOUBLE_DOOR_WIDTH) return true;
				if (size.width > size.depth) return true;
			}

			return (
				size.width < (constraints.width.min ?? 0) ||
				size.width > (constraints.width.max ?? 0) ||
				size.height < (constraints.height.min ?? 0) ||
				size.height > (constraints.height.max ?? 0) ||
				size.depth < (constraints.depth.min ?? 0) ||
				size.depth > (constraints.depth.max ?? 0)
			);
		},
		[thickness?.parts, state.thickness],
	);

	const getMaterial = useCallback(
		(materialProps: MaterialProps) => {
			const hoverMaterial = new MeshPhongMaterial({ color: 0x236376 });
			const errorMaterial = new MeshPhongMaterial({ color: 0xd32f2f });
			const isError = getIsError(materialProps);

			if (mode === "view") {
				return defaultMaterial;
			}

			return isError ? errorMaterial : materialProps.hover ? hoverMaterial : defaultMaterial;
		},
		[defaultMaterial, getIsError, mode],
	);

	return (
		<MaterialContext.Provider
			value={{
				defaultMaterial,
				getMaterial,
			}}
		>
			{props.children}
		</MaterialContext.Provider>
	);
}
